import { type FC, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { supabase } from "@/shared/lib/supabase";
import { EventType, trackEvent } from "@/shared/utils/core";
import { trackOnboardingStarted } from "@/shared/utils/funnelAnalytics";

import { ClientInviteLanding } from "./components/ClientInviteLanding";
import { ClientInviteError } from "./components/ClientInviteError";

import type { ClientInvitationData } from "./clientInviteTypes";

export const ClientInvitePage: FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const { data, isLoading, error } = useQuery({
    queryKey: ["client-invite", token],
    queryFn: async (): Promise<ClientInvitationData> => {
      if (!token) throw new Error("No invitation token provided");

      const { data: result, error: fetchError } = await supabase.functions.invoke(
        "account/lookup-client-invite",
        { body: { token } },
      );

      if (fetchError || result?.error) throw new Error("invalid");

      const invitation = result as ClientInvitationData;

      if (invitation.status === "accepted") throw new Error("accepted");
      if (invitation.status === "cancelled") throw new Error("invalid");

      const expiresAt = new Date(invitation.expires_at);
      if (expiresAt < new Date()) throw new Error("expired");

      return invitation;
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const viewTrackedRef = useRef(false);
  useEffect(() => {
    if (!viewTrackedRef.current && !isLoading && (data || error || !token)) {
      viewTrackedRef.current = true;
      const message = error instanceof Error ? error.message : "";
      const inviteState = data
        ? "valid"
        : message === "expired" || message === "accepted"
          ? message
          : "invalid";
      trackEvent(EventType.CLIENT_INVITE_VIEWED, {
        flow: "attorney_invite",
        invite_state: inviteState,
        is_existing_user: data?.has_account,
        has_recommended_plan: data ? Boolean(data.recommended_plan) : undefined,
      });
      if (data) {
        trackOnboardingStarted("attorney_invite");
        trackEvent(EventType.ONBOARDING_STEP_VIEWED, {
          flow: "attorney_invite",
          step_id: "client-invite-intro",
          step_number: 1,
          is_resume: false,
        });
      }
    }
  }, [data, error, isLoading, token]);

  if (!token) {
    return <ClientInviteError type="invalid" />;
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-subtle">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (error) {
    const message = (error as Error).message;
    if (message === "expired") return <ClientInviteError type="expired" />;
    if (message === "accepted") return <ClientInviteError type="accepted" />;
    return <ClientInviteError type="invalid" />;
  }

  if (!data) {
    return <ClientInviteError type="invalid" />;
  }

  return (
    <ClientInviteLanding
      clientEmail={data.client_email}
      lawyerName={data.lawyer_name}
      firmName={data.firm_name}
      avatarUrl={data.avatar_url}
      isExistingUser={data.has_account}
      token={token}
    />
  );
};
